import { Dispatch, useCallback } from 'react'
import { NavigateFunction } from 'react-router-dom'
import { toast } from 'react-toastify'
import ROUTES from 'src/constants/routes'
import { getAuthErrorMessage, getValidationErrorMessage } from 'src/constants/errorMessages'
import { API } from 'src/services/config'
import { LOGIN_STATUS } from '../constants'
import { AuthAction } from '../reducers/auth.reducer'
import { createUserSession } from '../services/auth.service'
import { storageService } from '../services/storage.service'
import { calculateTokenExpiry } from '../services/token.service'
import { isConfirmed, needsEmailConfirmation } from '../utils/confirmation.utils'

interface UseLoginProps {
  dispatch: Dispatch<AuthAction>
  navigate: NavigateFunction
  scheduleTokenRefresh: () => void
}

export function useLogin({ dispatch, navigate, scheduleTokenRefresh }: UseLoginProps) {
  return useCallback(
    async (loginUsername: string, senha: string, skipNavigation = false) => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true })
        dispatch({ type: 'SET_STATUS_LOGIN', payload: LOGIN_STATUS.LOADING })

        if (!loginUsername || !senha) {
          const error = new Error('Usuário e senha são obrigatórios')
          toast.error(getValidationErrorMessage(error))
          dispatch({ type: 'SET_LOADING', payload: false })
          dispatch({ type: 'SET_STATUS_LOGIN', payload: LOGIN_STATUS.ERROR })
          return
        }

        storageService.clearSession()
        dispatch({ type: 'SET_STATUS_LOGIN', payload: '' })

        const response = await createUserSession(loginUsername, senha)

        const { usuario, authenticationResult, fazendaConfiguracao } = response

        const expiresToken = calculateTokenExpiry(authenticationResult.expiresIn)

        storageService.saveSession({
          user: usuario,
          farmConfig: fazendaConfiguracao,
          token: authenticationResult.accessToken,
          refreshToken: authenticationResult.refreshToken,
          expiresIn: expiresToken.toString()
        })

        API.defaults.headers['Authorization'] = `Bearer ${authenticationResult.accessToken}`

        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            user: usuario,
            farmConfig: fazendaConfiguracao,
            token: authenticationResult.accessToken
          }
        })

        scheduleTokenRefresh()

        if (!skipNavigation) {
          const passwordResetRequired =
            usuario.senhaResetada === 'S' || usuario.senhaResetada === 'true' || usuario.senhaResetada === 'True'

          if (passwordResetRequired) {
            navigate(ROUTES.AUTH.CHANGE_PASSWORD)
          } else if (isConfirmed(usuario.telefoneConfirmado) && needsEmailConfirmation(usuario.emailConfirmado)) {
            navigate(ROUTES.AUTH.CONFIRM_EMAIL)
          } else {
            navigate(ROUTES.ROOT)
          }
        }
      } catch (error: any) {
        dispatch({ type: 'SET_LOADING', payload: false })
        dispatch({ type: 'SET_STATUS_LOGIN', payload: LOGIN_STATUS.ERROR })
        toast.error(getAuthErrorMessage(error))
      }
    },
    [dispatch, navigate, scheduleTokenRefresh]
  )
}
